// Fastly CDN image delivery — all rasters live on fast.yoreflix.com.
// Webv2 uses the 1080 variant (the 720 split was TV-only).
// SVG (logo/icons), fonts and token CSS are bundled, not served from here.

export const CDN_BASE = 'https://fast.yoreflix.com'

export type CdnCategory =
  | 'general'
  | 'previews/content'
  | 'thumbnails/content'
  | 'thumbnails/content/tv'
  | 'thumbnails/events'
  | 'thumbnails/events/tv'
  | 'previews/events'
  | 'carousel'
  | 'thumbnails'
  | 'explore'
  | 'promo'
  | 'playframes'
  | 'playposition'

/** Build a CDN image URL. `res` (e.g. '1080') is inserted when provided. */
export function cdnImg(category: CdnCategory, file: string, res?: string): string {
  const parts = ['img', category, res, file].filter(Boolean)
  return `${CDN_BASE}/${parts.join('/')}`
}

/** Brand / UI rasters: TextureBg.jpg, logoani.gif, etc. (img/general/) */
export const generalImg = (file: string) => cdnImg('general', file)

/** Content posters at 1080 (img/previews/content/1080/) */
export const posterImg = (file: string) => cdnImg('previews/content', file, '1080')

/** Carousel art at 1080 (img/carousel/1080/) */
export const carouselImg = (file: string) => cdnImg('carousel', file, '1080')

/** Content thumbnails (img/thumbnails/content/) — the timebar collage tiles (ThumbnailImage). */
export const contentPreview = (file: string) => cdnImg('thumbnails/content', file)

/**
 * ★ THE TV'S OWN THUMBNAIL VARIANT (img/thumbnails/content/tv/). Same `ThumbnailImage` filename,
 * a separate rendition — and roughly **a third of the bytes** (37 KB vs 110 KB for a typical card).
 *
 * This is not a micro-optimisation on a television. A Categories screen holds six rails of these, and
 * the device is a mid-range ARM chip on someone's living-room wifi: at ~70 KB saved per card, using the
 * web's rendition costs several megabytes of decode and transfer for pictures displayed at a fraction
 * of the size. v1 uses the `tv/` folder everywhere for exactly this reason — it is a deliberate choice,
 * not an accident of its asset pipeline, and it is the one place the TV should NOT follow the web.
 */
export const tvContentPreview = (file: string) => cdnImg('thumbnails/content/tv', file)

/** Event thumbnails (img/thumbnails/events/) — the timebar event cards + inline
 *  article images (ThumbnailImage / a paragraph's Image). */
export const eventPreview = (file: string) => cdnImg('thumbnails/events', file)

/**
 * ★ THE TV'S EVENT THUMBNAIL (img/thumbnails/events/tv/) — the events counterpart of
 * `tvContentPreview`, and the same reasoning applies on a living-room device.
 *
 * ⚠ NOTHING IN THIS REPO DRAWS IT YET. It is here because the files demonstrably exist — all 15
 * live events return 200 (measured 2026-08-07, with a bogus filename as the control) — so a cache
 * purge that ignored them would leave whatever does fetch them showing the old picture. TVv2 draws
 * events through `eventPreview`; the native TV apps are the likely reader.
 */
export const tvEventPreview = (file: string) => cdnImg('thumbnails/events/tv', file)

/** Event preview art (img/previews/events/) — the event-group drill-in thumbnail
 *  strip (an EventEntry's PreviewImage). ⚠ NO resolution segment: unlike content posters there
 *  is no 720 or 1080 variant, and both probe 404 on every live event. */
export const eventPosterImg = (file: string) => cdnImg('previews/events', file)

/** Catalog Article inline images (img/explore/) — a paragraph's Image on an
 *  EntryType:'Article' entry (Webv1 resolved content-article images from here). */
export const exploreImg = (file: string) => cdnImg('explore', file)

// Entry-animation assets (item 0)
export const TEXTURE_BG = generalImg('TextureBg.jpg')
export const LOGO_ANIMATION = generalImg('logoani.gif')

/** The green-marble field behind the timebar. */
export const TIMEBAR_BG = generalImg('timebarbggreen.jpg')

/**
 * Filmstrip scrubber frame — `img/playframes/<key>/thumbnail_NNNN.jpg`. The folder
 * is the content entry's PrimaryKey (the TV `viewercontrols` keys these on
 * `videoDetails.PrimaryKey`, NOT the slug — verified live 2026-06-30); a 4-digit
 * zero-padded index, one frame per 10s.
 */
export const playframeImg = (key: string, index: number): string =>
  `${CDN_BASE}/img/playframes/${key}/thumbnail_${String(index).padStart(4, '0')}.jpg`

/**
 * ★ THE PLAYPOSITION STRIP — `img/playposition/<key>/thumbnail_NNNN.jpg`, one frame per **15s**.
 *
 * v1's own player builds its scrubber from here (`monza/public/endpoints/_common/js/video.js`
 * sets `thumbnailImageBaseUrl = "https://fast.yoreflix.com/img/playposition/"`), and the v1 TV
 * apps stay live indefinitely. So this is NOT a stale duplicate of `playframes/` — it is a second,
 * coarser strip that live clients are still fetching. Frame 0000 is byte-identical between the two
 * folders because both start at t=0; frame 0001 is not (measured 2026-08-07: different ETag,
 * 9471 vs 7310 bytes).
 */
export const playpositionImg = (key: string, index: number): string =>
  `${CDN_BASE}/img/playposition/${key}/thumbnail_${String(index).padStart(4, '0')}.jpg`

/**
 * Player poster (paused-frame fallback) — `img/playposition/<key>/videoplayer1080.jpg`.
 *
 * ⛔ THE FOLDER IS `playposition`, NOT `playframes`. This built the `playframes/` path until
 * 2026-08-07, which **404s on every title** — proved against the live CDN, with a bogus filename
 * as the control (the CDN does return 404 for a missing object, so the 200s are real). It had no
 * callers, so nothing shipped broken; the first thing to use it would have drawn a broken image.
 */
export const playerPosterImg = (key: string): string =>
  `${CDN_BASE}/img/playposition/${key}/videoplayer1080.jpg`
